/**
 * The status words: every word a surface prints about the STATE of something —
 * a claim's source reading, how its path ended, whether it has been checked or
 * its field surveyed, a result recorded on it, the evidence version it stands
 * on, a proposal that has already run, a price cell that cannot print a price,
 * what one of a run's moves came to, the colour a node wears — in one module.
 *
 * One table because a status word is a claim about the record, and D112 found
 * five of them each saying something other than what was stored. A word written
 * inline at every surface drifts one surface at a time; a word read from here is
 * one edit, and a surface that wants a new state has to add a row that the other
 * surfaces can then see and reuse instead of inventing a synonym for. The card,
 * the thread's strip, the node tree, the legend, the coach, the announcer and
 * every export read these; `vocabulary.test.ts` fails the suite, naming file and
 * line, on a string literal or a run of JSX text anywhere in `src/` that is one
 * of these words written out again.
 *
 * Words, not sentences. A refusal is a sentence and belongs to the press that
 * raises it (`actRefusal`, `cueRefusal`), where the button and the paragraph are
 * one reading. What lives here is the one- or three-word state a cell or an
 * eyebrow carries, and the one clause a status line strings together.
 *
 * Casing is the table's, never the surface's to reinvent. Every word here is
 * capitalised as it opens a cell or an eyebrow, and a surface that sets one
 * mid-line lowercases it — except the words that only ever sit inside a line
 * (`ENDING_LABEL`, `OBJECTION_STOOD_BELOW`, `CLOSENESS_WORD`,
 * `PRIOR_ART_CLOSENESS_WORD`, `SWEEP_KIND_LABEL`, `PREDICTION_FARED`,
 * `EXECUTED_READING_LABEL`), which are lower case, and which the card
 * capitalises when one opens it.
 *
 * Nearly a leaf: it imports the money formatter and nothing else at runtime, and
 * that module imports nothing at runtime, so anything in `core/` or `ui/` can
 * read this. The types come in as `import type`, which `verbatimModuleSyntax`
 * erases. `types.ts`, `endings.ts` and `panel.ts` re-export the tables that used
 * to live in them, so their old imports keep working; that makes types →
 * vocabulary → models a runtime edge, and it is safe for the reason above —
 * `models.ts` imports nothing at runtime, so the edge cannot close a cycle. Keep
 * it that way: a runtime import from `types.ts` here WOULD close one. The
 * conjecture line's tables moved here too (D287), and `conjecture.ts`, `sweep.ts`,
 * `executableConjecture.ts` and `priorArt.ts` re-export theirs for the same
 * reason — so each of those now imports this module at runtime, and the same rule
 * holds for them: their types come in here as `import type`, and nothing else
 * does. `vocabulary.test.ts` fails on any other runtime import.
 */

import type { BriefChange } from './brief'
import type { ContradictionVerdict } from './contradiction'
import type { Ending } from './endings'
import type { ExecutedReading } from './executableConjecture'
import type { LinkReading } from './link'
import { fmtUsd } from './models'
import type { PanelChip } from './panel'
import type { PriorArtReading } from './priorArt'
import type { SweepKind } from './sweep'
import type {
  Channel,
  OpKind,
  Outcome,
  PanelVerdict,
  PriorArt,
  PriorArtEntry,
  SourceDesign,
  SourceReading,
  Stance,
  StanceTally,
  WebSource,
} from './types'
import type { WaitCause } from '../engine/providers/contract'

export const STATUS_WORD = {
  /** A proposal whose verb has run on its target since it was proposed — read off
   *  the stored ops and records, never off what the model said (`core/feed.ts`). */
  done: 'Done',
  /** A proposal whose verb is running on its target right now. Also the word a
   *  screen reader hears beside an exploration row's ring while its run is going
   *  (D334) — the same state, said of the exploration rather than the proposal. */
  running: 'Running',
  /** An exploration whose run finished, or whose answer landed while the reader
   *  was elsewhere, since it was last opened here — the word beside a row's
   *  filled dot (D334). */
  unread: 'Unread',
  /** A proposal whose verb was pressed under the lock and waits on the Up next
   *  line behind the pass in flight — the line's own eyebrow (`ui/UpNext.tsx`). */
  upNext: 'Up next',
  /** A paid press on a model with no key: pressing leads to the key prompt, so a
   *  dollar figure would price a call that will not happen. */
  needsKey: 'Needs a key',
  /** A press the worked example backs with an authored branch: it spends nothing. */
  demo: 'Part of the demo',
  /** A press that opens a form or edits locally: no model runs. */
  free: 'No model call',
  /** A key the provider accepted on Save's free check (`engine/keyCheck.ts`). */
  keyWorks: 'Key works',
  /** A key written with nothing to vouch for it yet: no free call could be made,
   *  or none answered. The first pass is its check. */
  keySaved: 'Key saved',
  /** What the coach is reading, as its card's heading: the graph a run just left,
   *  with nothing pressed since — `coachHeading` in `ui/coachView.ts`. */
  afterRun: 'After the run',
  /** …or the graph as it stands, after a pass, a seed's opening, or by hand. The
   *  coach's quiet line counts in the same words (`worthALook`). It was "Worth a
   *  look", which reads as the app's verdict on the claims; what the card holds is
   *  an order to read them in, and the heading now says so. The key keeps its old
   *  name so the surfaces that read it did not move. */
  worthALook: 'What to look at next',
  /** An exploration opened from somebody's share link and not yet continued
   *  (`SharedCopy.readFirst`): the read-first line's eyebrow, and the tag on its
   *  row in the sidebar. */
  sharedCopy: 'Shared copy',

  /* ── where a claim stands: the card's status line, the thread's strip and
     ancestor cards, the node tree's accessible names ─────────────────────── */

  /** A claim with no reading and no recorded result: it has met nothing outside
   *  the model. Said out loud rather than left to an absence (D157). The tree
   *  said "not checked" for the same record while the card said this. */
  notChecked: 'Not checked yet',
  /** Said after a reading whose check read the claim's EARLIER wording
   *  (`verificationLive` false): a check of other words is not a check of these. */
  checkedBeforeRewrite: 'Checked before a rewrite',
  /** The seed's clause: a survey is on record. The card follows it with the
   *  survey's date. A question is not a claim a check settles (D143). */
  fieldSurveyed: 'Field surveyed',
  /** The seed's clause when no survey is on record. */
  fieldNotSurveyed: 'Field not surveyed yet',
  /** The Overview's connections section before any scan has read the graph:
   *  no pair proposed, because nothing has looked (`ui/Breakthroughs.tsx`). */
  notScanned: 'Not scanned yet',
  /** A result somebody recorded against the claim (`node.outcome`) — said where
   *  it settled nothing, since one that settled it is already the ending. The
   *  findings outline said "Observed outcome recorded" for the same record. */
  resultRecorded: 'Result recorded',
  /** The findings outline's middle class: a live source check, no result. */
  checkRecorded: 'Source check recorded',
  /** The findings outline's last class: neither a result nor a live check. Not
   *  "not checked yet" — a check that read an earlier wording lands here too. */
  proposedClaim: 'Proposed claim',
  /** A claim on the before side of an evidence rebase: a newer branch head was
   *  authored from the changed record without deleting this one (D65). */
  priorEvidence: 'Prior evidence version',
  /** A claim whose evidence record changed after its reasoning was authored,
   *  with no rebase yet. Never "wrong", "invalid" or "refuted": a check is a
   *  bounded reading of a search (D65). The thread said "needs evidence rebase"
   *  for the same record while the card said this. */
  needsSecondLook: 'Needs a second look',
  /** A claim reasoned, through the claims above it, from one a live source check
   *  or a recorded result has since refuted, and not re-derived since
   *  (`core/taint.ts`, `refutedUnder`). The report's section had always called
   *  these "Claims standing on something refuted" while the card, the strip, the
   *  path and the tree said only "needs a second look" — the words of any changed
   *  record — or, under a refuting RESULT, which moves no source record, nothing
   *  at all. Never "refuted" alone: the claim itself was not. */
  standsOnRefuted: 'Stands on something refuted',
  /** A claim the reader rewrote after something above it was refuted: her words
   *  came after the record, so the taint walk does not mark it as owed a
   *  re-derivation (`rewrittenOver`), but the claim it hangs under is still
   *  refuted and the card says so. Not "stands on something refuted", whose
   *  sentence is "reasoned before … and not re-derived since" — false of words
   *  typed after the record was on the card. */
  rewrittenOverRefuted: 'Rewritten by you since something above it was refuted',
  /** A live check cited a passage from a work Crossref's retraction data reports
   *  withdrawn (`retractedWorks`). The coach, the passage's title and the
   *  reference list said so; the card, the strip, the path and the tree read
   *  "Sources appear to agree" and nothing else. Not a reading: the publisher took
   *  back part of what the check read, which is not a verdict on the claim. */
  citesRetracted: 'Cites a retracted work',
  /** The person rewrote the claim themselves (`editedAt`). The press is "Rewrite
   *  this understanding" and the export always said "Rewritten by you", while
   *  the card said "revised" about the same record. Wherever the node's author
   *  is named, it rides with who first proposed it (`rewriteAuthorship`). */
  revisedByYou: 'Rewritten by you',
  /** An open gap the person answered with a given or a constraint in the
   *  standing brief (`briefAnswerOf`). Answered, not settled: the gap's ending is
   *  still the model's, and a given is stipulated, never certified. */
  answeredInBrief: 'Answered in your brief',
  /** A synthesis joining two branches (`bridgeFrom`). */
  bridge: 'Bridge',
  /** A leaf that carries only the model's reasoning kernel (`compact`). */
  compactLeaf: 'Compact leaf',
  /** A compact leaf a later pass detailed; the card follows it with the model. */
  detailedBy: 'Detailed by',
  /** Part of the node's text was not retained. */
  textShortened: 'Text shortened',
  /** A node that is no longer on the graph, where a surface still names it. */
  pruned: 'Pruned',
  /** A stored structured comparison of two claims whose wording or scope record
   *  has moved since it was made (`comparisonStale` in `core/disagreement.ts`) —
   *  the Lab's Findings tab says the same of the same condition in a sentence. */
  comparisonStale: 'Inputs changed since',

  /* ── a conjecture: the Insights fold's filters, its card's facts, the
     coach's metric ── */

  /** A conjecture no prior-art search has read under the wording it carries now
   *  — never searched, or searched only before a rewrite. One state, and it had
   *  three names: the ledger's filter said "Not searched", its card "not searched
   *  for prior art" and the coach's metric "UNSEARCHED". This one says which
   *  search, as the card's did. It names a search not run and nothing more: a
   *  conjecture nobody has searched for is not one nobody has made (invariant 12). */
  notSearchedForPriorArt: 'Not searched for prior art',
  /** No result recorded against it (`node.outcome` absent), as the Insights
   *  fold's filter says it. The pair of `resultRecorded`, whose words the filter
   *  reads for the other half — it said "Has a result" beside a card that said
   *  "Result recorded" about the same record. */
  noResultYet: 'No result recorded yet',
  /** No challenge and no contradiction hangs off it (`conjectureStanding`), as its
   *  card's "Through so far" line says it (`throughSoFar`, D291). A zero said out
   *  loud, as `notChecked` is (D157): a line that dropped the clause would read as
   *  nothing to report. It counts what hangs off the conjecture and nothing more —
   *  a cross-examination's verdict and a panel are clauses of their own. */
  notChallenged: 'Not challenged yet',
  /** No experiment hangs off it: the count of tests proposed under it is zero, on
   *  the same line. "Under it" because the card also says whether an executable
   *  test was proposed FOR it in Research Lab, which is not a node, and the ledger
   *  said "no test proposed yet" and "an executable test proposed" in one breath. */
  noTestProposed: 'No test proposed under it yet',

  /* ── the price cell and the running figure ───────────────────────────── */

  /** The next of these presses is expected to carry the exploration past the cap
   *  (it still runs — the cap recolours, it does not block). */
  wouldExceedCap: 'Would exceed your cap',
  /** The exploration's figure already stands past the cap. */
  pastCap: 'Past your cap',

  /* ── a run ───────────────────────────────────────────────────────────── */

  /** A run holding at its confirm gate with a move proposed. The pod said this
   *  while the thread's gate said "Proposed — the run waits for your go". */
  awaitingGo: 'Proposed, waiting on you',
  /** A live run between passes: nothing in flight, the policy picking where to go.
   *  The run row prints it where the move in flight would stand (`ui/RunStrip.tsx`). */
  choosingMove: 'Choosing the next move',
  /** What one move came to, when it came to no node (`moveResult`). */
  nothingNew: 'Nothing new',
  declined: 'Declined',
  didNotRun: 'Did not run',
  incomplete: 'Incomplete',
  /** A conjecture pass that landed none: an answer the pass is allowed to give,
   *  not a branch with nothing left in it, so never "nothing new". The run's row,
   *  the pass's toast and the voice's digest say it in these words; the count when
   *  some landed is `conjecturesProposed`. "Insight" is the reader's word for a
   *  conjecture (D333); the key keeps the stored kind's name. */
  noConjectureLanded: 'No insight landed',
  /** A cross-examination that recorded no specific criticism. */
  reviewedNoObjection: 'Reviewed, no specific objection',

  /* ── whether a provider's response was cut off ─────────────────────── */

  truncated: 'Truncated',
  notTruncated: 'Not truncated',
  /** An older record that never stored whether the response was cut off. */
  truncationUnrecorded: 'Status unrecorded',
} as const

export type StatusWord = keyof typeof STATUS_WORD

/**
 * `refuted` is plural because the bar is. One opened page was once enough, and
 * the label kept that page's singular after invariant 2 raised the bar to two
 * independent sites with verified quotes, or three identified works: "A source
 * states the opposite" told a reader one site had said so, under a reading no one
 * site can earn.
 *
 * `mixed` is a MIXED READING rather than "Sources disagree", because the check
 * prompt sends three different situations there — sources that disagree with each
 * other, sources that back half the claim and dispute the other half, and any
 * refutation whose page could not be opened — and one of them is sources AGREEING
 * with each other, against the claim. `readingNote` says which, from the stances.
 *
 * `unsourced` is not "No source behind it", which read as "there is no evidence".
 * It means the check's citations were not among the search results and were set
 * aside: a fact about the check, not about the claim.
 *
 * `not-checkable` is not "Not a claim sources settle", which is true of only one
 * of the two records the reading holds. The pass can say the claim is a step
 * inside an argument, and the gate lands an absence there when part of the search
 * failed, which says nothing about whether sources could settle the claim. Both
 * are a check that could not check it; `readingNote` says which, from the record
 * (`demotedFor`). Not "No reading" either, which elsewhere means a claim nothing
 * has looked at (D326).
 */
export const READING_LABEL: Record<SourceReading, string> = {
  supported: 'Sources appear to agree',
  mixed: 'Mixed reading',
  refuted: 'Sources state the opposite',
  'nothing-found': 'Nothing found',
  'not-checkable': "Couldn't be checked",
  unsourced: "Sources couldn't be confirmed",
}

/**
 * A link scan's reading, on the chip.
 *
 * Every one of them names what the SEARCH did. None of them names what it means
 * for the pair — `unjoined` in particular says a search came back empty and
 * stops there, because "nobody has connected these" is a claim about the
 * literature and the tool only ever saw a page of results.
 */
export const LINK_READING_LABEL: Record<LinkReading, string> = {
  'already-made': 'Already connected',
  unsound: 'Not every search finished',
  thin: 'Too little came back',
  'no-intermediate': 'No shared term',
  unjoined: 'No joining work found',
}

/**
 * A prior-art search's reading, on a conjecture (`core/priorArt.ts` derives it).
 * Written rather than templated, and never stronger than the note beneath it —
 * like the link scan's, every one names what the SEARCH did. Too few pages back
 * is one state whichever search it happened to, so `thin` is the link scan's own
 * row rather than a second one that could drift from it.
 */
export const PRIOR_ART_READING_LABEL: Record<PriorArtReading, string> = {
  stated: 'Stated before',
  'partly-stated': 'Partly stated',
  unsound: 'The search did not complete',
  thin: LINK_READING_LABEL.thin,
  'not-found': 'Not found by this search',
}

/**
 * `stated`'s label where every page taking the step dates itself after the
 * wording it searched was registered (`priorArtLabel`): "before" would be false
 * of every page the reading rests on. The same reading under another label, so
 * it is not a row of the table above — the ceiling on the re-read rating holds
 * either way, and nothing about it says the conjecture came first.
 */
export const PRIOR_ART_STATED_SINCE = 'Stated elsewhere since'

/** What one panel seat ruled on the claim. `panel.ts` decides; this names it. */
export const PANEL_VERDICT_LABEL: Record<PanelVerdict, string> = {
  holds: 'Holds',
  'holds-weaker': 'Holds, weaker',
  fails: 'Fails',
  undecidable: 'Cannot reach it',
}

/** What a panel as a whole reads as, on its chip (`panelChip`). What this app is
 *  allowed to call a panel is the panel arithmetic's business, and a label edited
 *  on a surface would be a claim edited without its rule. A conjecture's card
 *  says the same state in these words, lower-cased mid-line (`throughSoFar`): its
 *  ledger said "the panel split" and "the panel agreed" for two of these three,
 *  and "agreed" for the third as well (D291). */
export const PANEL_CHIP_LABEL: Record<PanelChip, string> = {
  disagreed: 'The lenses disagreed',
  agreed: 'The lenses agreed',
  'too-few-decided': 'Too few lenses decided',
}

/** How close the work a check found sits to the claim (`core/priorWork.ts`). */
export const CLOSENESS_WORD: Record<PriorArt['closeness'], string> = {
  same: 'the same thing',
  overlapping: 'overlapping',
  adjacent: 'adjacent',
}

/**
 * How close a page a prior-art search listed sits to the conjecture it searched,
 * in the words that search's brief defines each by — the card's list and both
 * documents' (`core/priorArt.ts`). They were two identical copies, one in the card
 * and one in the markdown, which agreed by being copied.
 *
 * Not `CLOSENESS_WORD`, though the keys are spelled alike. That table is the
 * source check's, whose brief measures work against a CLAIM ("already states, or
 * already does, the specific thing"); this search's brief measures a page against
 * a conjecture's STEP and PREDICTION, and `same` here means it takes the step or
 * makes the prediction for the same reason — a claim has no step to take. Two
 * instruments with two definitions keep two sets of words, each the one its
 * model was given.
 */
export const PRIOR_ART_CLOSENESS_WORD: Record<PriorArtEntry['closeness'], string> = {
  same: 'takes the same step',
  overlapping: 'states part of it',
  adjacent: 'same field, another claim',
}

/**
 * What a field sweep's item reports (`core/sweep.ts`), lower case, as the card,
 * the feed, both documents, the voice and a conjecture pass's block set it before
 * the item's host or sentence. Each names what the PAGE reports, never a finding
 * about the field.
 */
export const SWEEP_KIND_LABEL: Record<SweepKind, string> = {
  'open-question': 'open question',
  anomaly: 'anomaly',
  dispute: 'dispute',
  'failed-replication': 'failed replication',
  'recent-shift': 'recent shift',
}

/** Which way a recorded result went, against the claim it was recorded on. */
export const BEARING_LABEL: Record<Outcome['bearing'], string> = {
  supported: 'The result went the way the claim needed',
  refuted: 'The result went against the claim',
  inconclusive: 'The result did not settle it',
}

/**
 * How a conjecture's prediction fared against one result, lower case, as a
 * clause — the track record's words (D282), which `trackRecordLine` says in its
 * own counted sentence, and the words a recorded result's reading and an executed
 * receipt's are built from, so those two cannot drift apart. "Went the way the
 * prediction said", never "confirmed": one observation is one observation, and a
 * prediction that came true once has come true once.
 */
export const PREDICTION_FARED = {
  predicted: 'went the way the prediction said',
  against: 'went against the prediction',
  unsettled: 'did not settle it',
} as const

/**
 * A recorded result, read against a conjecture's prediction, as a conjecture's
 * card opens a line with it. The conjecture's counterpart of `BEARING_LABEL`,
 * which reads a result against what a CLAIM needed; a conjecture made a
 * prediction, and its result is read against that.
 */
export const RESULT_READING: Record<Outcome['bearing'], string> = {
  supported: `A result you recorded ${PREDICTION_FARED.predicted}`,
  refuted: `A result you recorded ${PREDICTION_FARED.against}`,
  inconclusive: `A result you recorded ${PREDICTION_FARED.unsettled}`,
}

/**
 * What one executed receipt read about the prediction (`executedSentence`), lower
 * case, after "Executed <day>:". The track record's words for the same reason as
 * `RESULT_READING`: one execution is one execution — and a receipt is not a result
 * anybody recorded, so these never become `RESULT_READING`'s (D283).
 */
export const EXECUTED_READING_LABEL: Record<ExecutedReading, string> = {
  predicted: PREDICTION_FARED.predicted,
  against: `${PREDICTION_FARED.against}, landing where the expectation it departs from said`,
  unsettled: PREDICTION_FARED.unsettled,
  'no-reading': 'gave no reading',
}

/**
 * The Insights fold's filters (`ui/Overview.tsx`), which were the ledger's,
 * each the word the record already has for the fact it filters on, so a filter
 * and the cards under it say one thing. Recorded facts only: `not-found` is a
 * reading a search wrote, in that reading's own label — a list of places to
 * look, never a merit, and the filter had said "Not found by a search" beside
 * cards that said "not found by this search". `all` is not a state, and is not a
 * status word.
 */
export const CONJECTURE_FILTER_LABEL = {
  all: 'All',
  unsearched: STATUS_WORD.notSearchedForPriorArt,
  'not-found': PRIOR_ART_READING_LABEL['not-found'],
  'no-result': STATUS_WORD.noResultYet,
  result: STATUS_WORD.resultRecorded,
} as const

/**
 * How a claim ended, lower case, as the words sit inside a status line or a
 * sentence. What each ending means, and why "refuted" and "dead end" are none of
 * them, is `core/endings.ts`, which decides them.
 */
export const ENDING_LABEL: Record<Ending, string> = {
  'objection-stood': 'objection stood',
  open: "open: reasoning can't settle it",
  'path-failed': 'path failed',
  answered: 'answered by your result',
}

/** "2 objections stood", "1 path failed" — a count with its ending, for a
 *  sentence. The plural of each `ENDING_LABEL`, beside it so that a renamed
 *  ending cannot keep its old plural; the labels stay singular everywhere else. */
export function endingCount(e: Ending, n: number): string {
  switch (e) {
    case 'objection-stood':
      return `${n} objection${n === 1 ? '' : 's'} stood`
    case 'path-failed':
      return `${n} path${n === 1 ? '' : 's'} failed`
    case 'open':
      return `${n} open: reasoning can't settle ${n === 1 ? 'it' : 'them'}`
    case 'answered':
      return `${n} ${ENDING_LABEL.answered}`
  }
}

/** "cites a retracted work", "cites 2 retracted works": `citesRetracted` with its
 *  count, lower case, as a status line carries it. Works, not passages — one paper
 *  quoted three times is one thing a publisher took back (`retractedWorks`). */
export const citesRetractedCount = (n: number): string =>
  n === 1 ? STATUS_WORD.citesRetracted.toLowerCase() : `cites ${n} retracted works`

/** What a parent shows when an objection below it stood. "In reasoning" is the
 *  point of the sentence: it is the model's argument, not a record. */
export const OBJECTION_STOOD_BELOW = 'an objection below it stood, in reasoning'

/** The channels — the colour a node wears — in the canvas legend's words, which
 *  the step rail prints too. Two vocabularies for one hue is how a colour stops
 *  meaning anything, so the legend's rows read this rather than restating it.
 *  `insight` names the CHANNEL, which insights (stored as `conjecture`), takeaways
 *  (stored as `insight`) and syntheses all wear (`kindChannelOf`), so it keeps
 *  "Insight" while the kinds read `KIND_LABEL`; the legend row glosses all three
 *  (D333). */
export const CHANNEL_WORD: Record<Channel, string> = {
  signal: 'Argued',
  weak: 'Weak',
  tension: 'Contradiction',
  gap: 'Gap',
  insight: 'Insight',
  refuted: 'Disputed',
  dead: 'Closed',
}

/**
 * What a structured comparison of two claims came to — the Lab's own four
 * verdicts (`core/contradiction.ts` decides them, by closed rules over the scope a
 * model extracted), capitalised, so the card's "Why these disagree" and the Tests
 * tab it links to name one verdict with one word.
 */
export const COMPARISON_VERDICT_LABEL: Record<ContradictionVerdict, string> = {
  genuine: 'Genuine contradiction',
  'scope-mismatch': 'Scope mismatch',
  compatible: 'Compatible',
  underdetermined: 'Underdetermined',
}

/**
 * "5 reports · 1 independent": a claim's reports and the independence units the
 * Lab's evidence genealogy counts them as (`core/claimEvidence.ts`). The clause
 * the card's status line carries, in the Evidence tab's own two nouns.
 */
export const reportsIndependent = (reports: number, independent: number): string =>
  `${reports} report${reports === 1 ? '' : 's'} · ${independent} independent`

/**
 * What kind of evidence a source is, as the check that admitted it read the page
 * (`WebSource.design`), singular and plural, in the order the card lists them.
 * The nouns a reader asked in: "primary paper or coverage?". A news report here
 * covers commentary too; the check's prompt says so to the model.
 */
export const DESIGN_WORD: Record<SourceDesign, readonly [string, string]> = {
  trial: ['trial', 'trials'],
  observational: ['observational study', 'observational studies'],
  review: ['review or meta-analysis', 'reviews or meta-analyses'],
  model: ['model or simulation', 'models or simulations'],
  primary: ['primary text', 'primary texts'],
  news: ['news report', 'news reports'],
}

/** Reports a check could not label, counted beside the ones it could — and, in
 *  the stance strip, sources the check gave no side at all. One word for one
 *  state, a label the check did not give, whichever label it was. */
export const DESIGN_UNLABELLED = 'not labelled'

/**
 * How a kept source bears on the claim, as the check labelled it
 * (`WebSource.stance`), in the form a count takes: "3 dispute". A tally's words
 * rather than a sentence's, so one reads the same beside a 1 as beside a 5 — "1
 * dispute", "0 support". `context` is the check's own word for a source that is
 * relevant and takes no side; the source list prints nothing for one, and the
 * strip counts it so the three add up to what was kept.
 */
export const STANCE_WORD: Record<Stance, string> = {
  disputes: 'dispute',
  supports: 'support',
  context: 'context',
}

/**
 * One kept source's side, as a surface that names it per source prints it: the
 * check's own word, or `DESIGN_UNLABELLED` for a source the check gave no side
 * at all. That source is STORED as `context` so the three stances still add up
 * (`stanceUnlabelled` marks it), and a line that printed the stored word would
 * say the check had called it context — a side the check did not take. The
 * strip counts it under the same word, so a list and its strip agree.
 */
export const stanceLabel = (s: Pick<WebSource, 'stance' | 'stanceUnlabelled'>): string =>
  s.stanceUnlabelled && s.stance === 'context' ? DESIGN_UNLABELLED : s.stance

/** One count of the strip: which stance, how many, and its words ("3 dispute"). */
export interface StancePart {
  stance: Stance | 'unlabelled'
  n: number
  text: string
}

/**
 * "3 dispute · 0 support · 2 context": the kept sources' stances as one line, the
 * card's check and the feed's check result both. Always the three, in that order,
 * zeros included — "0 support" under a mixed reading is the finding, and a strip
 * that dropped its zeros would change shape from card to card. Sources the check
 * gave no stance are counted last, in the word the table already has for a label
 * a check did not give, and only where there are any. Empty with nothing kept.
 * In parts, each with its stance, so the card can mark each count with the rule
 * its rows wear in the source list.
 *
 * A count says THAT sources disagree, never on what (D126): the summary says on
 * what, and the strip does not try to.
 */
export function stanceParts(t: StanceTally): StancePart[] {
  if (!t.kept) return []
  const parts: StancePart[] = (['disputes', 'supports', 'context'] as const).map((stance) => ({
    stance,
    n: t[stance],
    text: `${t[stance]} ${STANCE_WORD[stance]}`,
  }))
  if (t.unlabelled)
    parts.push({
      stance: 'unlabelled',
      n: t.unlabelled,
      text: `${t.unlabelled} ${DESIGN_UNLABELLED}`,
    })
  return parts
}

/** The strip as one line of text, for a surface that prints it without marks. */
export const stanceStrip = (t: StanceTally): string | null =>
  t.kept
    ? stanceParts(t)
        .map((p) => p.text)
        .join(' · ')
    : null

/**
 * "5 reports · 1 independent: 1 observational study, 4 news reports": the
 * independence count (`reportsIndependent`) and what kind of evidence the reports
 * are, as ONE clause of the card's status line — or the kinds alone, "1 trial",
 * where one report is not a count. Reports the check did not label are counted as
 * such rather than left for the reader to subtract. Null where there is nothing to
 * say: no label, and one report or none.
 */
export function evidenceKinds(
  tally: { reports: number; independent: number } | undefined,
  kinds: ReadonlyMap<SourceDesign, number>,
): string | null {
  const parts: string[] = []
  let labelled = 0
  for (const design of Object.keys(DESIGN_WORD) as SourceDesign[]) {
    const n = kinds.get(design) ?? 0
    if (!n) continue
    labelled += n
    parts.push(`${n} ${DESIGN_WORD[design][n === 1 ? 0 : 1]}`)
  }
  const counted =
    tally && tally.reports > 1 ? reportsIndependent(tally.reports, tally.independent) : null
  if (!parts.length) return counted
  const rest = (tally?.reports ?? 0) - labelled
  if (rest > 0) parts.push(`${rest} ${DESIGN_UNLABELLED}`)
  return counted ? `${counted}: ${parts.join(', ')}` : parts.join(', ')
}

/**
 * What a request met when the API or the wire turned it away, as the line of a
 * pass waiting to ask again leads with it (`waitLine` in `core/failure.ts`), and
 * the run strip's while a run waits out a failed move (`runPauseLine`). A state,
 * so one word per cause: the sentence after it is the line's own.
 */
export const WAIT_WORD: Record<WaitCause, string> = {
  rate: 'Rate limited',
  overloaded: 'API overloaded',
  server: 'API server error',
  network: 'Connection failed',
}

/**
 * What happened to the standing brief, as the exploration's thread says it on the
 * quiet line set between the passes it fell between (`briefChanges` in
 * `core/brief.ts`). "Your brief", as `answeredInBrief` says it: the reader's
 * directives, not the app's.
 */
export const BRIEF_CHANGE_WORD: Record<BriefChange['what'], string> = {
  added: 'Added to your brief',
  edited: 'Edited in your brief',
  removed: 'Removed from your brief',
}

/**
 * The four lines of a run's end card (`ui/RunFound.tsx`, `core/runFound.ts`), each
 * the name of the report section its line opens — "What held up", "What didn’t
 * survive", "Still open" — so a line and the section it points at say one thing.
 * `heldUp` is `core/heldUp.ts`'s word, and it is the calibration ledger's word for
 * the same record: a tested claim that "held up" is a result that went its way.
 */
export const FOUND_WORD = {
  heldUp: 'Held up',
  didNotSurvive: 'Didn’t survive',
  open: 'Still open',
  /** What a run would do with more budget, off the run's own state. */
  next: 'Next',
} as const

export type FoundLine = keyof typeof FOUND_WORD

/* ── the conjecture passes in the feed and the run card (D289) ──

   What the feed says of an executable test, and the run card's conjecture line's
   word. A pass's count and its empty answer are `conjecturesProposed` and
   `STATUS_WORD.noConjectureLanded` above, and a conjecture no search has read is
   `STATUS_WORD.notSearchedForPriorArt`. */

/** An executable test's contract that no execution has run (D283): it waits in
 *  Research Lab, where running it is the person's press. */
export const EXECUTABLE_NOT_RUN = 'Not run yet'

/** An executable-test pass that left no contract — an empty answer, a proposal
 *  the client refused, a refusal or a failure. Its message says which. */
export const NO_TEST_ADDED = 'No test added'

/** The Insights section's title — the report's, the Overview fold's — and the
 *  word of the run card's conjecture line (`core/runFound.ts`) that opens it. One
 *  word, so the pointer and the thing it points at cannot drift apart. The section
 *  id stays `conjectures`; only the reader's word moved (D333). */
export const FOUND_CONJECTURES_WORD = 'Insights'

/**
 * Every word above, flat, for the one test that holds the rule. The sentence
 * builders (`endingCount`, `citesRetractedCount`, `pressEstimate`, `worthALook`,
 * `reportsIndependent`, `evidenceKinds`, `stanceParts`, `stanceStrip`, `quotedPress`,
 * `modelCount`, `claimCount`, `conjecturesProposed`) are not words and are not
 * listed, and neither is `CONJECTURE_FILTER_LABEL`, whose states are rows of
 * tables listed here and whose `all` is not a state.
 */
export const ALL_STATUS_WORDS: readonly string[] = [
  ...Object.values(STATUS_WORD),
  ...Object.values(READING_LABEL),
  ...Object.values(LINK_READING_LABEL),
  // Less `thin`, which is the link scan's row and listed there.
  ...Object.values(PRIOR_ART_READING_LABEL).filter((w) => w !== LINK_READING_LABEL.thin),
  PRIOR_ART_STATED_SINCE,
  ...Object.values(PANEL_VERDICT_LABEL),
  ...Object.values(PANEL_CHIP_LABEL),
  ...Object.values(CLOSENESS_WORD),
  ...Object.values(PRIOR_ART_CLOSENESS_WORD),
  // Less `dispute`, which is `STANCE_WORD`'s spelling and listed there. Two
  // states, one spelling — a page reporting a dispute, a source disputing a
  // claim — and a one-word lower-case word is a key's spelling the scan never
  // reads, so the second row would hold nothing the first does not.
  ...Object.values(SWEEP_KIND_LABEL).filter((w) => w !== STANCE_WORD.disputes),
  ...Object.values(BEARING_LABEL),
  // Less `unsettled`. "Did not settle it" is not the conjecture line's: it is how
  // any inconclusive result is said — the Record form's tab, a result's line in a
  // pass's prompt — and `BEARING_LABEL.inconclusive` is that state's row. Listed
  // bare, it would claim those as copies of a prediction's clause; listed in full,
  // in `RESULT_READING` and there, it holds what is the conjecture line's.
  PREDICTION_FARED.predicted,
  PREDICTION_FARED.against,
  ...Object.values(RESULT_READING),
  // Less the rows that are `PREDICTION_FARED`'s, listed or declined above.
  ...Object.values(EXECUTED_READING_LABEL).filter(
    (w) => !(Object.values(PREDICTION_FARED) as string[]).includes(w),
  ),
  ...Object.values(ENDING_LABEL),
  OBJECTION_STOOD_BELOW,
  ...Object.values(CHANNEL_WORD),
  ...Object.values(COMPARISON_VERDICT_LABEL),
  ...Object.values(DESIGN_WORD).flat(),
  DESIGN_UNLABELLED,
  ...Object.values(STANCE_WORD),
  ...Object.values(WAIT_WORD),
  ...Object.values(BRIEF_CHANGE_WORD),
  ...Object.values(FOUND_WORD),
  EXECUTABLE_NOT_RUN,
  NO_TEST_ADDED,
]

/**
 * What one press is expected to add, as an accessible name says it: "est.
 * +$0.30". The visible cell prints the before → after pair (`VerbPrice`); a name
 * read aloud wants the one number the press itself buys. Never a cost — the same
 * estimate that leans high, worded as one (invariant 5).
 */
export const pressEstimate = (usd: number): string => `est. +${fmtUsd(usd)}`

/**
 * A press named with its object, as its button names it: "Rethink “Grid
 * storage”", and on the Up next line "Rewrite “Heat pumps cut bills”" — a
 * rewrite's editor has no name of its own to carry there, so the line names it
 * the way the rethink's button already did. The button, the line, and the
 * toasts and refusals about either press read this one form, so a press waiting
 * on the line goes by the name of the button that put it there.
 */
export const quotedPress = (verb: string, object: string): string => `${verb} “${object}”`

/** Whether a provider's response was cut off, lower case, as the clause after
 *  "response" reads it — one function because the review coverage and the
 *  experiment revision print the same three states off the same field. */
export const truncationWord = (truncated: boolean | null): string =>
  (truncated === null
    ? STATUS_WORD.truncationUnrecorded
    : truncated
      ? STATUS_WORD.truncated
      : STATUS_WORD.notTruncated
  ).toLowerCase()

/** "1 model", "2 models": a count of models, as the sidebar's model row says how
 *  many of the models its routes name hold a key ("1 model · by role"). It said
 *  "1 models" with one key stored and one route to another company. */
export const modelCount = (n: number): string => `${n} model${n === 1 ? '' : 's'}`

/** "1 claim", "12 claims": a count of claims, as the cross-examination's press
 *  says how many it would review. It said "Reviews up to 1 claims" on a leaf. */
export const claimCount = (n: number): string => `${n} claim${n === 1 ? '' : 's'}`

/** "1 insight proposed", "3 insights proposed": what a conjecture pass that
 *  landed some came to, lower case, as a run's row says it after its dash
 *  (`moveResult`) — the count beside `noConjectureLanded`, so a pass that landed
 *  some and one that landed none are said in one vocabulary wherever either is.
 *  "Proposed", always: an insight here is a proposal nothing outside the model has
 *  tested (D333, invariant 12). */
export const conjecturesProposed = (n: number): string =>
  `${n} insight${n === 1 ? '' : 's'} proposed`

/** The noun a pass's landed nodes are counted in. A panel lands one dissent,
 *  and it is an objection wherever it is said; every other pass that lands nodes
 *  lands claims. One place, so the announcer (`landingLine`) and the pass's own
 *  toast (`endOp`) cannot name one landing two ways (D166, D335). */
export const landedNoun = (kind: OpKind): string => (kind === 'panel' ? 'objection' : 'claim')

/** "2 claims landed", "1 objection landed", "3 insights proposed": what a
 *  finished pass left beside its targets, as its toast leads with it (D335). An
 *  insight pass says its proposals as its own row does. */
export const passLanded = (kind: OpKind, n: number): string =>
  kind === 'conjecture'
    ? conjecturesProposed(n)
    : `${n} ${landedNoun(kind)}${n === 1 ? '' : 's'} landed`

/** The coach's one line when nothing of it is open: how many things, in the
 *  heading's own words, so the line and the card it leads to say one thing —
 *  "3 things to look at next", the heading with its count in place of "what". */
export const worthALook = (n: number): string =>
  `${n} ${n === 1 ? 'thing' : 'things'} ${STATUS_WORD.worthALook.replace(/^What /, '').toLowerCase()}`

/* ── workstream 1: the conjecture presses (D286) ── */

/**
 * What a finished pass drew, as its toast says it: "est. +$0.12 · $0.40 so far",
 * against the cap where one is set ("… · $0.40 of $5.00 so far"), and led by
 * "Past your cap" once the exploration is over it — with `over` for the toast's
 * tone, which turns to a warning there (D15). `endOp` says it for every verb; the
 * passes whose ending is a sentence of their own — the scan, the link search,
 * the prior-art search, the field sweep — carry it inside that sentence. One
 * wording for both, because the second copy said "+$0.12 spent": a figure with
 * no "est." and no cap, the two things D112 and invariant 5 ask of every figure.
 */
export function chargeLine(
  delta: number,
  soFar: number,
  cap: number | null,
): { line: string; over: boolean } {
  const over = cap !== null && soFar > cap
  const line =
    cap !== null
      ? `${over ? `${STATUS_WORD.pastCap} · ` : ''}est. +${fmtUsd(delta)} · ${fmtUsd(soFar)} of ${fmtUsd(cap)} so far`
      : `est. +${fmtUsd(delta)} · ${fmtUsd(soFar)} so far`
  return { line, over }
}
